import { supabase } from '../../shared/supabase/supabaseClient';

const WEBP_CONTENT_TYPE = 'image/webp';

/**
 * Patrones de error de Storage que indican un problema transitorio
 * (recuperable con retry). Errores permanentes (RLS, payload too large)
 * no se retryean.
 */
const TRANSIENT_ERROR_PATTERNS = [
  'database schema is invalid or incompatible',
  'timeout',
  'timed out',
  'network',
  'failed to fetch',
  'transient',
  '503',
  '502',
  '500',
  'internal server error',
  'service unavailable',
  'temporarily unavailable',
];

function isTransientStorageError(message: string): boolean {
  const lower = message.toLowerCase();
  return TRANSIENT_ERROR_PATTERNS.some((p) => lower.includes(p));
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Sube un archivo a Supabase Storage con retry para errores transitorios.
 *
 * Backoff: 500ms después del 1er intento, 2s después del 2do.
 * Máximo 3 intentos (1 original + 2 retries). Errores permanentes
 * (RLS, payload too large) no se retryean — se propagan inmediatamente.
 *
 * Regression: VUELTO-NEGOCIO-29. Storage podía quedar temporalmente
 * inconsistente ("database schema is invalid or incompatible") y el
 * upload fallaba sin retry, dejando al usuario sin poder subir imágenes.
 */
export async function uploadToStorageWithRetry(params: {
  bucket: string;
  path: string;
  body: Uint8Array;
  contentType?: string;
  upsert?: boolean;
  maxRetries?: number;
}): Promise<void> {
  const { bucket, path, body, contentType = WEBP_CONTENT_TYPE, upsert = true, maxRetries = 2 } = params;

  let lastError: { message: string } | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(path, body, { contentType, upsert });

    if (!uploadError) return;

    lastError = uploadError;

    // No retryear errores permanentes (RLS, payload too large, etc.)
    if (!isTransientStorageError(uploadError.message)) {
      throw new Error(uploadError.message);
    }

    // Último intento: propagar el error sin más retries
    if (attempt === maxRetries) {
      throw new Error(uploadError.message);
    }

    // Backoff exponencial: 500ms, 2s, ...
    const backoffMs = 500 * Math.pow(4, attempt);
    await delay(backoffMs);
  }

  // No debería llegar acá, pero por safety:
  throw new Error(lastError?.message ?? 'Upload failed');
}

/**
 * Decodifica base64 a Uint8Array para subir a Storage.
 */
export function decodeBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Obtiene la URL pública de un archivo en Storage.
 */
export function getPublicUrl(bucket: string, path: string): string {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}
